import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';

type Company = {
  id: string;
  name: string;
  mcp_token: string | null;
};

export default function CompanyMcpSettings() {
  const { theme } = useTheme();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const current = useMemo(() => companies.find(c => c.id === selected) || null, [companies, selected]);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('company_configs')
        .select('id, name, mcp_token')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      setCompanies(data as Company[]);
      if (data && data.length && !selected) setSelected(data[0].id);
    } catch (e) {
      console.error('Failed to load companies', e);
    } finally {
      setLoading(false);
    }
  }

  function generateToken(): string {
    const bytes = new Uint8Array(32);
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
      window.crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  async function rotate() {
    if (!current) return;
    if (!confirm('Rotate MCP token for this company? Existing MCP clients will need the new token.')) return;
    const token = generateToken();
    const { error } = await supabase
      .from('company_configs')
      .update({ mcp_token: token })
      .eq('id', current.id);
    if (error) {
      alert('Failed to rotate token: ' + error.message);
      return;
    }
    await load();
    alert('New token generated and saved.');
  }

  async function clearToken() {
    if (!current) return;
    if (!confirm('Clear MCP token for this company? MCP clients will be blocked.')) return;
    const { error } = await supabase
      .from('company_configs')
      .update({ mcp_token: null })
      .eq('id', current.id);
    if (error) {
      alert('Failed to clear token: ' + error.message);
      return;
    }
    await load();
  }

  if (loading) {
    return (
      <div className="p-6">
        <div 
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: theme.colors.primaryColor }}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>Company MCP Settings</h1>
        <p style={{ color: theme.colors.textSecondary }}>Manage per-company MCP access tokens for ElevenLabs Agents and other MCP clients.</p>
      </div>

      {/* About Banner */}
      <div 
        className="rounded-lg p-4"
        style={{ backgroundColor: theme.colors.infoBg, border: `1px solid ${theme.colors.infoBorder}` }}
      >
        <h3 className="font-semibold" style={{ color: theme.colors.infoText }}>About this page</h3>
        <ul className="mt-2 text-sm list-disc pl-5 space-y-1" style={{ color: theme.colors.infoText }}>
          <li>Purpose: issue and manage per-company MCP access tokens used by ElevenLabs Agents and other MCP-compatible clients.</li>
          <li>Storage: token is saved in <code className="font-mono">company_configs.mcp_token</code> for the selected company.</li>
          <li>Generate/Rotate: creates a new random 32-byte hex token and overwrites the existing one (old tokens stop working).</li>
          <li>Clear: sets the token to <code className="font-mono">NULL</code>, effectively blocking all MCP clients for that company.</li>
          <li>Security: treat this token like a password. Share it with clients via header 
            <code className="ml-1 px-1 py-0.5 rounded text-xs font-mono" style={{ backgroundColor: theme.colors.bgHover, color: theme.colors.textPrimary }}>
              X-Connector-Token: {"{token}"}
            </code>.
          </li>
          <li>Integration: the token is used by ElevenLabs Agent Manager and any MCP gateway connecting to your tools/knowledge.</li>
          <li>Recommendation: keep tokens enabled only as needed and rotate on suspected leakage or personnel changes.</li>
        </ul>
      </div>

      <div
        className="rounded-lg p-6 space-y-6"
        style={{
          backgroundColor: theme.colors.bgCard,
          border: `1px solid ${theme.colors.borderColor}`
        }}
      >
        {/* Company Selection */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
            Select Company
          </label>
          <select
            className="w-full px-3 py-2 rounded-lg focus:ring-2"
            style={{
              backgroundColor: theme.colors.bgCard,
              border: `1px solid ${theme.colors.borderColor}`,
              color: theme.colors.textPrimary
            }}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* MCP Token Section */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
            MCP Token
          </label>
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                className="flex-1 px-3 py-2 rounded-lg font-mono text-sm"
                style={{
                  backgroundColor: theme.colors.bgHover,
                  border: `1px solid ${theme.colors.borderColor}`,
                  color: theme.colors.textPrimary
                }}
                value={current?.mcp_token || ''}
                placeholder="No token generated yet"
              />
              <button
                onClick={rotate}
                className="px-4 py-2 rounded-lg transition-colors font-medium"
                style={{
                  backgroundColor: theme.colors.warningBg,
                  color: theme.colors.warningText,
                  border: `1px solid ${theme.colors.warningBorder}`
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f59e0b'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.warningBg}
              >
                {current?.mcp_token ? '🔄 Rotate' : '🔑 Generate'}
              </button>
              {current?.mcp_token && (
                <button
                  onClick={clearToken}
                  className="px-4 py-2 rounded-lg transition-colors font-medium"
                  style={{
                    backgroundColor: theme.colors.dangerBg,
                    color: theme.colors.dangerText,
                    border: `1px solid ${theme.colors.dangerBorder}`
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.dangerBg}
                >
                  🗑️ Clear
                </button>
              )}
            </div>

            {current?.mcp_token && (
              <div className="p-3 rounded-lg" style={{ backgroundColor: theme.colors.infoBg, border: `1px solid ${theme.colors.infoBorder}` }}>
                <p className="text-sm" style={{ color: theme.colors.infoText }}>
                  <strong>🔗 Usage:</strong> Share this token with your MCP client as header:
                  <code className="ml-2 px-2 py-1 rounded text-xs font-mono" style={{
                    backgroundColor: theme.colors.bgHover,
                    color: theme.colors.textPrimary
                  }}>
                    X-Connector-Token: {current.mcp_token}
                  </code>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
